import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Auth uses an httpOnly cookie set by the backend; send it on every request
axios.defaults.withCredentials = true;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [geofenceBlocked, setGeofenceBlocked] = useState(false);
  const [geofenceMessage, setGeofenceMessage] = useState('');
  const [checkingGeofence, setCheckingGeofence] = useState(false);

  useEffect(() => {
    const restoreSession = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`);
        setUser(response.data);
      } catch (error) {
        setUser(null);
      } finally {
        setLoading(false);
      }
    };
    restoreSession();
  }, []);

  // If the session cookie expires mid-use, log the user out cleanly instead of
  // leaving pages failing with fetch errors
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        const url = error.config?.url || '';
        const isAuthRoute = url.includes('/auth/login') || url.includes('/auth/me') || url.includes('/auth/register');
        if (error.response?.status === 401 && !isAuthRoute) {
          setUser(null);
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, []);

  const checkGeofence = useCallback(() => {
    if (!navigator.geolocation) {
      return;
    }

    setCheckingGeofence(true);

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const response = await axios.post(`${API}/geofence/check`, {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          });

          if (!response.data.allowed) {
            setGeofenceBlocked(true);
            setGeofenceMessage(`You are ${Math.round(response.data.distance_meters)}m away from the workplace. Access is restricted to within ${response.data.radius_meters}m.`);
          } else {
            setGeofenceBlocked(false);
            setGeofenceMessage('');
          }
        } catch (error) {
          // Don't block if check fails
          setGeofenceBlocked(false);
        } finally {
          setCheckingGeofence(false);
        }
      },
      () => {
        // If we can't get location and user is not admin, block them
        setGeofenceBlocked(true);
        setGeofenceMessage('Location access is required. Please enable location services to use this app.');
        setCheckingGeofence(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  }, []);

  // Check geofence when user is loaded
  useEffect(() => {
    if (user && user.role !== 'admin') {
      checkGeofence();
    } else if (user && user.role === 'admin') {
      setGeofenceBlocked(false);
    }
  }, [user, checkGeofence]);

  const login = async (username, password) => {
    const response = await axios.post(`${API}/auth/login`, { username, password });
    const { user: userData } = response.data;
    setUser(userData);
    return userData;
  };

  const register = async (username, password, role = 'manager') => {
    const response = await axios.post(`${API}/auth/register`, { username, password, role });
    const { user: userData } = response.data;
    setUser(userData);
    return userData;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (error) {
      // Cookie may already be expired; clear local state regardless
    }
    setUser(null);
    setGeofenceBlocked(false);
    setGeofenceMessage('');
  };

  const updateUser = (userData) => {
    setUser(userData);
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      loading, 
      login, 
      register, 
      logout, 
      updateUser, 
      isAdmin: user?.role === 'admin',
      geofenceBlocked,
      geofenceMessage,
      checkingGeofence,
      recheckGeofence: checkGeofence
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
